import { esc, openModal, closeModal, onModalClose, showModalErr } from '../ui/modal';
import { t } from '../ui/lang';
import { deviceAliases, appState } from '../core/state';
import { renderOfflineStage } from '../stream/inline-stream';
import { trashSvg } from '../stream/stream-controls-shared';
const downloadSvg = '<svg viewBox="0 0 16 16" width="14" height="14" fill="none" stroke="currentColor" stroke-width="1.4"><path d="M8 2v8M5 7l3 3 3-3M3 13h10"/></svg>';
export function showAddDeviceModal(farmId, onDone) {
    const modal = openModal(`
        <h2 class="modal__title">${t('farmdev.add_device')}</h2>
        <form class="modal__form" id="agg-add-form" novalidate>
            <div class="modal__field">
                <label>${t('farmdev.device_name')}</label>
                <input id="agg-name" type="text" maxlength="40" autocomplete="off" placeholder="${t('farmdev.device_name_ph')}" required />
            </div>
            <div id="agg-add-error" class="modal__error" style="display:none"></div>
            <div class="modal__actions">
                <button type="button" class="modal__cancel" id="agg-cancel">${t('common.cancel')}</button>
                <button type="submit" class="modal__submit">${t('common.create')}</button>
            </div>
        </form>
    `);
    modal.querySelector('#agg-cancel').addEventListener('click', closeModal);
    modal.querySelector('#agg-add-form').addEventListener('submit', async (e) => {
        e.preventDefault();
        const name = modal.querySelector('#agg-name').value.trim();
        const errorEl = modal.querySelector('#agg-add-error');
        const btn = modal.querySelector('.modal__submit');
        if (!name) {
            showModalErr(errorEl, t('farmdev.device_name_required'));
            return;
        }
        btn.disabled = true;
        try {
            const r = await fetch(`/api/farms/${farmId}/aggregator-devices`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({ name, host: window.location.origin }),
            });
            const data = await r.json();
            if (!r.ok || !data.udid || !data.qr) {
                showModalErr(errorEl, data.error ?? t('farmdev.failed_add_device'));
                btn.disabled = false;
                return;
            }
            closeModal();
            showDeviceQrModal(data.udid, data.name ?? null, data.qr, data.code);
            onDone();
        }
        catch {
            showModalErr(errorEl, t('farmdev.network_error'));
            btn.disabled = false;
        }
    });
}
/**
 * Manual-provisioning block shown next to the QR (on the pending card and in the
 * QR modal): the panel URL + device id for reference, plus the one provisioning
 * code (same payload as the QR, base64) to paste into the client. Empty when the
 * device has no QR (already activated). Pair with {@link wireProvCopy}.
 */
function provManualHtml(udid, code) {
    if (!code)
        return '';
    // The card centers its children, so the block must be forced to full card
    // width (width:100% + align-self:stretch + box-sizing) — otherwise the
    // nowrap code sizes the block to its content and overflows both edges.
    // Each value then truncates with an ellipsis (min-width:0 + overflow:hidden)
    // so the code fits on one line next to the Copy button; full value on title.
    const ell = 'display:block;flex:1 1 0;min-width:0;overflow:hidden;text-overflow:ellipsis;white-space:nowrap;opacity:0.85';
    const rowStyle = 'display:flex;gap:8px;width:100%;min-width:0';
    return `
        <div class="agg-prov" style="display:flex;flex-direction:column;gap:8px;width:100%;box-sizing:border-box;align-self:stretch;margin:8px 0;padding:10px 14px;font-size:0.9em">
            <div style="${rowStyle};align-items:baseline"><span style="flex:0 0 66px;opacity:0.6">${t('farmdev.device_id')}</span><code style="${ell}" title="${esc(udid)}">${esc(udid)}</code></div>
            <div style="${rowStyle};align-items:center"><code style="${ell}" title="${esc(code)}">${esc(code)}</code><button type="button" class="tbl-btn copy-key-btn agg-prov__copy" data-code="${esc(code)}" title="${t('farmdev.copy_full_code')}">⎘ ${t('common.copy')}</button></div>
        </div>`;
}
/** Wire the copy buttons of any {@link provManualHtml} blocks under [root]. */
function wireProvCopy(root) {
    root.querySelectorAll('.agg-prov__copy').forEach((btn) => {
        btn.addEventListener('click', () => {
            void navigator.clipboard.writeText(btn.dataset['code'] ?? '');
            btn.textContent = `✓ ${t('common.copied')}`;
            setTimeout(() => { btn.textContent = `⎘ ${t('common.copy')}`; }, 1500);
        });
    });
}
export function showDeviceQrModal(udid, name, qr, code) {
    const label = name || udid;
    const manual = code
        ? `<p class="modal__text" style="margin-top:14px">${t('farmdev.prov_manual_hint')}</p>${provManualHtml(udid, code)}`
        : '';
    const modal = openModal(`
        <h2 class="modal__title">${t('farmdev.scan_on_phone')}</h2>
        <p class="modal__text">${t('farmdev.scan_qr_hint', { name: `<strong>${esc(label)}</strong>` })}</p>
        <img src="${esc(qr)}" alt="${t('farmdev.device_qr_alt')}" class="totp-qr" style="width:100%;height:auto" />
        ${manual}
        <div class="modal__actions">
            <a class="modal__cancel" id="agg-qr-download" download="${esc(label)}.png" href="${esc(qr)}">${t('farmdev.download_qr')}</a>
        </div>
    `);
    // Explicit: the only .modal__cancel here is the QR download link, which the ✕ would
    // otherwise take for this card's dismissal.
    onModalClose(modal, closeModal);
    wireProvCopy(modal);
}
export function showDeleteAggregatorDeviceModal(farmId, udid, onDone) {
    const modal = openModal(`
        <h2 class="modal__title">${t('farmdev.delete_device')}</h2>
        <p class="modal__text">${t('farmdev.revoke_device_confirm', { udid: esc(udid) })}</p>
        <div id="agg-del-error" class="modal__error" style="display:none"></div>
        <div class="modal__actions">
            <button type="button" class="modal__cancel" id="agg-del-cancel">${t('common.cancel')}</button>
            <button type="button" class="modal__submit modal__submit--danger" id="agg-del-confirm">${t('common.delete')}</button>
        </div>
    `);
    modal.querySelector('#agg-del-cancel').addEventListener('click', closeModal);
    modal.querySelector('#agg-del-confirm').addEventListener('click', async () => {
        const errorEl = modal.querySelector('#agg-del-error');
        try {
            const r = await fetch(`/api/farms/${farmId}/aggregator-devices/${encodeURIComponent(udid)}`, { method: 'DELETE' });
            const data = await r.json();
            if (!r.ok) {
                showModalErr(errorEl, data.error ?? t('common.failed_to_delete'));
                return;
            }
            closeModal();
            onDone();
        }
        catch {
            showModalErr(errorEl, t('farmdev.network_error'));
        }
    });
}
/**
 * Fetch this aggregator farm's provisioned devices and append the ones missing
 * from the live grid: not-yet-connected phones as pending QR cards, and
 * activated-but-offline phones as offline cards with a delete action (kept visible
 * so stale devices can be pruned by hand). `liveUdids` are the udids already shown
 * as live cards, so we don't duplicate a phone that's currently connected.
 */
export async function injectAggregatorPendingCards(farmId, liveUdids) {
    let data;
    try {
        const r = await fetch(`/api/farms/${farmId}/aggregator-devices?host=${encodeURIComponent(window.location.origin)}`);
        if (!r.ok)
            return;
        data = await r.json();
    }
    catch {
        return;
    }
    // The grid may have been rebuilt (new snapshot) or navigated away while we awaited.
    const grid = document.getElementById('device-grid');
    if (!grid || !document.body.contains(grid))
        return;
    // The name is set at add-time and stored panel-side (never sent to the phone), so
    // the live card can only show it via the alias map — which is loaded once at
    // startup and wouldn't include a device provisioned later. Seed it here on every
    // poll, and patch any already-rendered live card header in place.
    for (const d of data) {
        const name = (d.name ?? '').trim();
        if (!name)
            continue;
        deviceAliases.set(d.udid, name);
        const nameEl = grid.querySelector(`.device-card[data-udid="${CSS.escape(d.udid)}"] .device-name`);
        if (nameEl && nameEl.textContent !== name)
            nameEl.textContent = name;
    }
    // This runs on every grid rebuild/poll, so drop any cards we appended last time
    // before re-rendering — otherwise each pass duplicates every card.
    grid.querySelectorAll('.device-card--pending, .device-card--agg-offline').forEach((el) => el.remove());
    // Pre-activation devices get a pending QR card (scan to activate). Activated
    // devices that are currently offline get a plain offline card with a delete
    // action — so a phone that connected once and then went away stays visible and
    // removable by hand, instead of silently vanishing from the grid.
    const pending = data.filter((d) => !d.online && !d.activated && !liveUdids.has(d.udid));
    const offline = data.filter((d) => !d.online && d.activated && !liveUdids.has(d.udid));
    if (!pending.length && !offline.length)
        return;
    // Replace the "No devices in this farm" empty-state if these cards are all we have.
    grid.querySelector('.empty')?.remove();
    for (const d of pending)
        grid.appendChild(makePendingDeviceCard(farmId, d));
    for (const d of offline)
        grid.appendChild(makeOfflineDeviceCard(farmId, d));
}
function makePendingDeviceCard(farmId, d) {
    const displayName = d.name || d.udid;
    const card = document.createElement('div');
    // Pending cards stay at full opacity (unlike real offline devices) so the QR
    // is crisp and scannable — the red dot + "pending" already signal the state.
    card.className = 'device-card device-card--pending';
    card.dataset['udid'] = d.udid;
    card.innerHTML = `
        <div class="device-card__header">
            <span class="status-dot status-dot--red"></span>
            <span class="device-name" title="${esc(d.udid)}">${esc(displayName)}</span>
            <span class="device-updated" title="${t('farmdev.pending_hint')}">${t('farmdev.pending')}</span>
            <div class="device-card__actions">
                ${d.qr ? `<a class="card-icon-btn agg-pending-dl" download="${esc(displayName)}.png" href="${esc(d.qr)}" title="${t('farmdev.download_qr')}">${downloadSvg}</a>` : ''}
                <button class="card-icon-btn agg-pending-del" title="${t('common.delete')}">${trashSvg}</button>
            </div>
        </div>
        <div class="device-card__player">
            ${d.qr
        ? `<img src="${esc(d.qr)}" alt="${t('farmdev.device_qr_alt')}" class="agg-pending-qr" title="${t('farmdev.scan_to_activate')}" />`
        : `<p class="empty">${t('farmdev.qr_unavailable')}</p>`}
        </div>
        ${provManualHtml(d.udid, d.code)}
    `;
    card.querySelector('.agg-pending-del')?.addEventListener('click', () => showDeleteAggregatorDeviceModal(farmId, d.udid, () => card.remove()));
    if (d.qr) {
        card.querySelector('.agg-pending-qr')?.addEventListener('click', () => showDeviceQrModal(d.udid, d.name, d.qr, d.code));
    }
    wireProvCopy(card);
    return card;
}
/**
 * Card for an activated aggregator device that is currently offline. Unlike a
 * pending card there's no QR (the phone is paired for good) — it keeps the device
 * visible as offline with a delete/revoke action, so an admin can prune stale phones
 * by hand rather than have them auto-removed, plus the offline control column (info /
 * clicker log / run history / registry) — none of which needs the phone to be up.
 */
function makeOfflineDeviceCard(farmId, d) {
    const displayName = d.name || d.udid;
    const card = document.createElement('div');
    card.className = 'device-card offline device-card--agg-offline';
    card.dataset['udid'] = d.udid;
    card.innerHTML = `
        <div class="device-card__header">
            <span class="status-dot status-dot--red"></span>
            <span class="device-name" title="${esc(d.udid)}">${esc(displayName)}</span>
            <span class="device-updated" title="${t('farmdev.agg_offline_hint')}">${t('farmdev.offline_badge')}</span>
            <div class="device-card__actions">
                <button class="card-icon-btn delete-btn agg-offline-del" title="${t('common.delete')}">${trashSvg}</button>
            </div>
        </div>
        <div class="device-card__player"></div>
    `;
    // This phone is in no live snapshot, so synthesize the descriptor the shared offline
    // panel expects from the last state it reported (farm_devices, joined into the list).
    renderOfflineStage(card.querySelector('.device-card__player'), offlineDescriptor(farmId, d));
    card.querySelector('.agg-offline-del')?.addEventListener('click', () => showDeleteAggregatorDeviceModal(farmId, d.udid, () => card.remove()));
    return card;
}
function offlineDescriptor(farmId, d) {
    const num = (v) => (v == null ? undefined : v);
    return {
        udid: d.udid,
        state: d.state || 'offline',
        'ro.product.model': d.model ?? '',
        'ro.product.manufacturer': d.manufacturer ?? '',
        'screen.state': 'unknown',
        'battery.level': num(d.battery_level),
        'wifi.signal.level': num(d.wifi_signal_level),
        'cellular.signal.level': num(d.cellular_signal_level),
        farmId,
        // This grid only ever renders the selected farm, so its name is the one in state
        // (avoids importing farms-view, which imports this module).
        farmName: (appState.selectedFarmId === farmId ? appState.selectedFarm : null) ?? undefined,
        lastSeen: d.last_seen ? new Date(d.last_seen).getTime() : undefined,
    };
}
